import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/store/authStore";
import { useBrandingStore } from "@/store/brandingStore";
import {
  DEFAULT_BRANDING,
  brandingAssetUrl,
  fetchMyBranding,
  fetchBrandingByDomain,
  type Branding,
} from "@/api/branding";
import { brandCssVariables } from "./brandColors";

function setFavicon(href: string | null): void {
  if (!href) return;
  const links = document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]');
  if (links.length === 0) {
    const link = document.createElement("link");
    link.rel = "icon";
    link.href = href;
    document.head.appendChild(link);
    return;
  }
  links.forEach((l) => {
    l.removeAttribute("sizes");
    l.removeAttribute("type");
    l.href = href;
  });
}

/** localStorage key for the last brand seen on THIS hostname (MPPI-00263).
 *
 * Keyed by hostname so one tenant's colours can never be applied on another's
 * domain, even in the same browser. */
const brandCacheKey = (): string =>
  `mppi:branding:v1:${typeof window !== "undefined" ? window.location.hostname : ""}`;

/**
 * Paint a brand onto the document.
 *
 * Sets the FULL derived variable set, not just two hex values. Until
 * 2026-08-28 this wrote `--brand-primary` and `--brand-accent` and nothing
 * consumed them — Tailwind had the colours compiled in as literals, so a
 * white-label tenant could not change a single pixel. Tailwind now resolves
 * `primary`/`accent` to these variables, which is what makes the 330
 * `*-primary` / `*-accent` usages in this app follow the tenant.
 */
export function applyBranding(b: Branding): void {
  const root = document.documentElement;
  const vars = brandCssVariables(b.primary_color, b.accent_color);
  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, value);
  }
  if (b.page_title) document.title = b.page_title;
  setFavicon(brandingAssetUrl(b.favicon_url));

  // Remember for the next visit so the first paint is already correct rather
  // than flashing the default brand while /branding resolves.
  try {
    window.localStorage.setItem(
      brandCacheKey(),
      JSON.stringify({
        primary_color: b.primary_color,
        accent_color: b.accent_color,
        page_title: b.page_title,
      }),
    );
  } catch {
    // Private browsing, or storage disabled. The cache is an optimisation;
    // losing it costs a flash, not correctness.
  }
}

/**
 * Apply the cached brand for this hostname, before any network request.
 *
 * Without this the first paint on a tenant domain is the DEFAULT brand — a
 * visible flash of another company's colours, which is worse on a white-label
 * product than on a normal one. A stale entry is corrected as soon as the
 * real response lands.
 */
export function applyCachedBranding(): void {
  try {
    const raw = window.localStorage.getItem(brandCacheKey());
    if (!raw) return;
    const cached = JSON.parse(raw) as Partial<Branding>;
    if (!cached.primary_color || !cached.accent_color) return;
    const root = document.documentElement;
    const vars = brandCssVariables(cached.primary_color, cached.accent_color);
    for (const [name, value] of Object.entries(vars)) {
      root.style.setProperty(name, value);
    }
    if (cached.page_title) document.title = cached.page_title;
  } catch {
    // Unparseable cache: fall through to the CSS defaults.
  }
}

/** Fetches the logged-in admin's group brand and applies it (CSS vars,
 *  title, favicon, store). Admin staff resolve via the `agents` table;
 *  unassigned staff fall back to the Mina Prime default — no change. */
export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setBranding = useBrandingStore((s) => s.setBranding);

  // Kept for the group a user belongs to — still useful context, and the
  // admin UI shows it — but it NO LONGER decides what the app looks like.
  const { data } = useQuery({
    queryKey: ["branding", "me"],
    queryFn: fetchMyBranding,
    enabled: isAuthenticated,
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });

  // THE DOMAIN DECIDES THE BRAND (MPPI-00263 / W6, operator decision
  // 2026-08-28). Always enabled, not just pre-auth.
  //
  // Previously the login page branded by domain and then switched to the
  // user's group once authenticated, so a Mina Prime user signing in at a Gulf
  // Token domain saw Gulf Token, then MPPI. Defensible, but it reads as a bug
  // — and the operator's reason settles it: the brand has to be right BEFORE
  // login, and a brand that changes on login is not one brand.
  const { data: domainData, isPending: domainPending } = useQuery({
    queryKey: ["branding", "by-domain"],
    queryFn: fetchBrandingByDomain,
    enabled: true,
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });

  useEffect(() => {
    // Domain first, always. The user's group (`data`) is only a fallback for a
    // host that matches no configured domain — otherwise an assigned user on
    // an unbranded host would get the seeded default instead of their brand.
    const b = domainData ?? data ?? DEFAULT_BRANDING;

    // Colours can apply immediately: a page painted in the default palette for
    // a moment is better than an unstyled one.
    applyBranding(b);

    // But WHICH BRAND THIS IS must wait for an actual answer. This effect runs
    // on first render, before either query resolves, so calling setBranding
    // here unconditionally marked branding RESOLVED as Mina Prime — and
    // PublicSiteGate, seeing a resolved default brand, rendered the full Mina
    // marketing site on a HouseKey domain until the real answer arrived
    // (MPPI-00290). "Not yet known" and "known to be the default" are
    // different states and were being collapsed into one.
    if (!domainPending) {
      setBranding(b);
    }
  }, [isAuthenticated, data, domainData, domainPending, setBranding]);

  return <>{children}</>;
}
